/**
 * deloSledilec.ts — merjenje aktivnega časa (Superadmin → Pregled dela)
 * [PATCH pregled-dela v3]
 *
 * Načelo: odjemalec hrani žige aktivnosti in ob zaključku odseka izračuna
 * aktivne milisekunde pri PETIH pragovih neaktivnosti hkrati. Prag je s tem
 * izbirnik v poročilu, ne lastnost podatka.
 *
 * Odpoved sledilnika NE SME nikoli podreti dela: vse je v try/catch,
 * pošiljanje v .catch(() => {}).
 *
 * --- spremembe glede na v2 -------------------------------------------------
 * ODSTRANJEN preimenuj(). Faza 'nalaganje' se ne izpelje več iz preimenovanja
 * prejšnjega odseka, ampak iz odprtja uvozne maske (showSop || showUpload v
 * DokumentiPage). Meritev 09.08.2026 je pokazala, zakaj: uvozni gumb je
 * dosegljiv tudi ob odprtem dokumentu, zato je varovalka v preimenuj()
 * pravilno zavrnila delo — in 'nalaganje' ni nastalo nikoli.
 *
 * Posledica: 60-sekundna delitev je spet samo varovalo pred padcem
 * brskalnika, ne nosilni element definicije.
 * ---------------------------------------------------------------------------
 */

import { api } from './api';
import { useAuthStore } from '../stores/authStore';

export type Faza = 'nalaganje' | 'pregled' | 'popravki' | 'prenos' | 'splosno';
export type Vir = 'zapri' | 'skrit' | 'interval' | 'odjava';

export interface Kontekst {
  companyId: string;
  docType?: string | null;
  documentId?: string | null;
  stDokumentov?: number | null;
}

/** Pragovi neaktivnosti v ms. Vrstni red se mora ujemati s stolpci akt_*_ms. */
const PRAGOVI = [2000, 5000, 15000, 30000, 60000];

/** Najmanjši razmik med zabeleženima žigoma. Omeji rast polja; na vsoto vrzeli ne vpliva. */
const ZRNO_MS = 250;

/** Prisilna delitev odseka — varovalo pred padcem brskalnika. */
const DELITEV_MS = 60_000;

const POSILJANJE_MS = 30_000;
const VRSTA_MEJA = 20;

/** Odsek, daljši od tega, je okvara; strežnik ga zavrne z istim pragom. */
const NAJVEC_BRUTO_MS = 4 * 60 * 60 * 1000;

interface Odsek {
  company_id: string;
  faza: Faza;
  doc_type: string | null;
  document_id: string | null;
  st_dokumentov: number | null;
  zacetek: string;
  konec: string;
  bruto_ms: number;
  akt_2s_ms: number;
  akt_5s_ms: number;
  akt_15s_ms: number;
  akt_30s_ms: number;
  akt_60s_ms: number;
  st_dogodkov: number;
  vir: Vir;
}

interface Tekoci {
  faza: Faza;
  kontekst: Kontekst;
  zacetek: number;
  /** Točka, od katere se meri prva vrzel. Ob pristnem začetku = zacetek;
   *  ob delitvi = zadnji resnični žig prejšnjega odseka (lahko je PRED zacetek). */
  sidro: number;
  zigi: number[];
}

let tekoci: Tekoci | null = null;
let vrsta: Odsek[] = [];
let shranjen: { faza: Faza; kontekst: Kontekst } | null = null;
let posluhVzpostavljen = false;
let casovnikDelitve: any = null;
let casovnikPosiljanja: any = null;

/* ------------------------------------------------------------------ */
/*  Izračun aktivnega časa                                             */
/* ------------------------------------------------------------------ */

/**
 * Aktivne ms pri vseh petih pragovih naenkrat.
 *
 * Vsaka vrzel med zaporednima dogodkoma prispeva min(vrzel, prag), ta
 * prispevek pa leži na ZAČETKU vrzeli — zato ga je treba obrezati na okno
 * [zacetek, konec] tega odseka.
 *
 * Prav to obrezovanje je razlog, da delitev odseka meritve ne napihuje:
 * 120 s neaktivnosti pri pragu 5 s da 5 s ne glede na to, ali je bila
 * razdeljena na dva odseka. Brez obrezovanja bi dala 10 s.
 */
function izracunajAktivne(zacetek: number, konec: number, sidro: number, zigi: number[]): number[] {
  const rez = [0, 0, 0, 0, 0];
  let prej = sidro;
  const tocke = zigi.concat([konec]);
  for (const z of tocke) {
    if (z <= prej) continue;
    const vrzel = z - prej;
    for (let i = 0; i < PRAGOVI.length; i++) {
      const aktivnoDo = prej + Math.min(vrzel, PRAGOVI[i]);
      const a = Math.max(prej, zacetek);
      const b = Math.min(aktivnoDo, konec);
      if (b > a) rez[i] += b - a;
    }
    prej = z;
  }
  return rez;
}

/* ------------------------------------------------------------------ */
/*  Beleženje dogodkov                                                 */
/* ------------------------------------------------------------------ */

function zabeleziDogodek() {
  if (!tekoci) return;
  const zdaj = Date.now();
  const zadnji = tekoci.zigi.length ? tekoci.zigi[tekoci.zigi.length - 1] : tekoci.sidro;
  if (zdaj - zadnji < ZRNO_MS) return;
  tekoci.zigi.push(zdaj);
}

const DOGODKI = ['keydown', 'click', 'wheel', 'scroll', 'mousemove', 'focus'];

function vzpostaviPosluh() {
  if (posluhVzpostavljen || typeof window === 'undefined') return;
  posluhVzpostavljen = true;

  for (const d of DOGODKI) {
    window.addEventListener(d, zabeleziDogodek, { passive: true, capture: true });
  }

  document.addEventListener('visibilitychange', () => {
    try {
      if (document.visibilityState === 'hidden') {
        // zapri in NE odpri novega — sicer bi zavihek v ozadju vsakih 60 s
        // ustvarjal prazne vrstice
        const t = tekoci;
        if (t) shranjen = { faza: t.faza, kontekst: t.kontekst };
        koncaj('skrit');
        posljiTakoj();
      } else if (shranjen) {
        const s = shranjen;
        shranjen = null;
        zacni(s.faza, s.kontekst);
      }
    } catch { /* sledilnik ne sme podreti strani */ }
  });

  window.addEventListener('pagehide', () => {
    try { koncaj('zapri'); posljiTakoj(); } catch { /* prazno */ }
  });

  casovnikPosiljanja = setInterval(() => { void posljiVrsto(); }, POSILJANJE_MS);
}

/* ------------------------------------------------------------------ */
/*  Javni vmesnik                                                      */
/* ------------------------------------------------------------------ */

function smemMeriti(): boolean {
  try {
    const vloga = (useAuthStore.getState() as any)?.role;
    // stranke ne merimo; nova pot zanjo tudi ni na seznamu dovoljenih v deps.py
    return vloga != null && vloga !== 'stranka';
  } catch {
    return false;
  }
}

/** Začne nov odsek. Če kakšen že teče, ga najprej pravilno zaključi. */
export function zacni(faza: Faza, kontekst: Kontekst) {
  try {
    if (!smemMeriti() || !kontekst?.companyId) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      shranjen = { faza, kontekst };
      return;
    }
    if (tekoci) koncaj('zapri');
    vzpostaviPosluh();

    const zdaj = Date.now();
    tekoci = { faza, kontekst, zacetek: zdaj, sidro: zdaj, zigi: [] };

    if (casovnikDelitve) clearTimeout(casovnikDelitve);
    casovnikDelitve = setTimeout(razdeli, DELITEV_MS);
  } catch { /* prazno */ }
}

/** Prisilna delitev. Nov odsek podeduje zadnji resnični žig kot sidro. */
function razdeli() {
  try {
    if (!tekoci) return;
    const t = tekoci;
    const zadnjiZig = t.zigi.length ? t.zigi[t.zigi.length - 1] : t.sidro;
    koncaj('interval');
    const zdaj = Date.now();
    tekoci = { faza: t.faza, kontekst: t.kontekst, zacetek: zdaj, sidro: zadnjiZig, zigi: [] };
    if (casovnikDelitve) clearTimeout(casovnikDelitve);
    casovnikDelitve = setTimeout(razdeli, DELITEV_MS);
  } catch { /* prazno */ }
}

/** Zaključi tekoči odsek in ga postavi v vrsto za pošiljanje. */
export function koncaj(vir: Vir = 'zapri') {
  try {
    if (!tekoci) return;
    const t = tekoci;
    tekoci = null;
    if (casovnikDelitve) { clearTimeout(casovnikDelitve); casovnikDelitve = null; }

    const konec = Date.now();
    const bruto = konec - t.zacetek;
    if (bruto <= 0 || bruto > NAJVEC_BRUTO_MS) return;

    const a = izracunajAktivne(t.zacetek, konec, t.sidro, t.zigi);
    const jeDok = t.faza === 'pregled' || t.faza === 'popravki';

    vrsta.push({
      company_id: t.kontekst.companyId,
      faza: t.faza,
      doc_type: t.kontekst.docType ?? null,
      document_id: jeDok ? (t.kontekst.documentId ?? null) : null,
      st_dokumentov: jeDok ? null : (t.kontekst.stDokumentov ?? null),
      zacetek: new Date(t.zacetek).toISOString(),
      konec: new Date(konec).toISOString(),
      bruto_ms: bruto,
      akt_2s_ms: Math.round(a[0]),
      akt_5s_ms: Math.round(a[1]),
      akt_15s_ms: Math.round(a[2]),
      akt_30s_ms: Math.round(a[3]),
      akt_60s_ms: Math.round(a[4]),
      st_dogodkov: t.zigi.length,
      vir,
    });

    if (vrsta.length >= VRSTA_MEJA) void posljiVrsto();
  } catch { /* prazno */ }
}

/* ------------------------------------------------------------------ */
/*  Pošiljanje                                                         */
/* ------------------------------------------------------------------ */

function vzemiVrsto(): Odsek[] {
  const v = vrsta;
  vrsta = [];
  return v;
}

function poPodjetjih(v: Odsek[]): Record<string, Odsek[]> {
  const m: Record<string, Odsek[]> = {};
  for (const o of v) (m[o.company_id] ||= []).push(o);
  return m;
}

/** Redno pošiljanje prek običajnega odjemalca (žeton doda prestreznik v api.ts). */
async function posljiVrsto() {
  const v = vzemiVrsto();
  if (!v.length) return;
  for (const [cid, odseki] of Object.entries(poPodjetjih(v))) {
    try {
      await api.post(`/delo/${cid}/odseki`, { odseki });
    } catch { /* meritev ni pomembnejša od dela; izgubljen paket se ne ponavlja */ }
  }
}

/**
 * Pošiljanje ob zaprtju zavihka.
 * fetch(keepalive), NE navigator.sendBeacon — sendBeacon ne zna nastaviti
 * glave Authorization, aplikacija pa uporablja žeton v glavi (lib/api.ts).
 */
function posljiTakoj() {
  try {
    const v = vzemiVrsto();
    if (!v.length) return;
    const zeton = localStorage.getItem('token');
    if (!zeton) return;

    const osnova = (api as any)?.defaults?.baseURL || '/api/v1';
    for (const [cid, odseki] of Object.entries(poPodjetjih(v))) {
      fetch(`${osnova}/delo/${cid}/odseki`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${zeton}` },
        body: JSON.stringify({ odseki }),
      }).catch(() => {});
    }
  } catch { /* prazno */ }
}

/** Ob odjavi: zapri, pošlji, pozabi vse. */
export function ponastavi() {
  try {
    koncaj('odjava');
    posljiTakoj();
    vrsta = [];
    tekoci = null;
    shranjen = null;
    if (casovnikDelitve) { clearTimeout(casovnikDelitve); casovnikDelitve = null; }
  } catch { /* prazno */ }
}

/** Izpostavljeno samo za teste. */
export const _zaTeste = { izracunajAktivne, PRAGOVI };

export const deloSledilec = { zacni, koncaj, ponastavi };
export default deloSledilec;
